import { Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  BottomSheet,
  Button,
  Card,
  ListRow,
  useToast,
} from '../../components';
import { useActions, useAppData } from '../../data';
import { formatCurrency } from '../../lib/currency';
import {
  clearAllReceipts,
  deleteReceipt,
  listAllReceipts,
  type StoredReceipt,
} from '../../lib/receiptStore';

type Props = {
  open: boolean;
  onClose: () => void;
};

/**
 * Settings → Manage Receipts. Lists every persisted receipt with a
 * thumbnail and the linked transaction summary. Single "Clear all"
 * destructive action with a confirm prompt.
 */
export function ManageReceiptsSheet({ open, onClose }: Props) {
  const data = useAppData();
  const actions = useActions();
  const toast = useToast();
  const [receipts, setReceipts] = useState<StoredReceipt[]>([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void listAllReceipts().then((r) => {
      if (!cancelled) setReceipts(r);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const totalBytes = receipts.reduce((s, r) => s + r.sizeBytes, 0);

  const handleClearAll = async () => {
    const ok =
      typeof window !== 'undefined'
        ? window.confirm('Remove every saved receipt? This cannot be undone.')
        : true;
    if (!ok) return;
    await clearAllReceipts();
    // Detach receiptId from every transaction that had one.
    for (const r of receipts) {
      actions.updateTransaction(r.txId, { receiptId: undefined });
    }
    setReceipts([]);
    toast.show({ message: 'All receipts removed', kind: 'info' });
  };

  const removeOne = async (r: StoredReceipt) => {
    await deleteReceipt(r.id);
    actions.updateTransaction(r.txId, { receiptId: undefined });
    setReceipts((prev) => prev.filter((x) => x.id !== r.id));
  };

  return (
    <BottomSheet open={open} onClose={onClose} title="Manage receipts">
      <div className="px-screen-x py-3 flex flex-col gap-3">
        <div className="text-[13px] text-text-secondary">
          {receipts.length} receipt{receipts.length === 1 ? '' : 's'} ·{' '}
          {(totalBytes / 1024).toFixed(0)} KB
        </div>

        {receipts.length === 0 ? (
          <Card flush elevation="sm" className="py-4">
            <p className="px-card-pad text-[13px] text-text-secondary">
              No receipts saved yet. Capture one from the Today tab.
            </p>
          </Card>
        ) : (
          <Card flush elevation="sm" className="py-1">
            {receipts.map((r, i) => {
              const tx = data.transactions.find((t) => t.id === r.txId);
              const cat = tx
                ? data.categories.find((c) => c.id === tx.categoryId)
                : null;
              return (
                <div key={r.id}>
                  {i > 0 ? (
                    <div className="h-px bg-surface-secondary mx-card-pad" />
                  ) : null}
                  <ListRow
                    icon={
                      <img
                        src={r.thumbB64}
                        alt="Receipt"
                        className="w-7 h-7 rounded-button object-cover"
                      />
                    }
                    iconBgClass="bg-transparent"
                    iconColorClass="text-transparent"
                    title={tx?.description ?? '(deleted)'}
                    subtitle={
                      tx
                        ? `${cat?.name ?? '—'} · ${tx.date}`
                        : 'Linked transaction missing'
                    }
                    value={
                      tx
                        ? formatCurrency(tx.amount, {
                            currency: data.profile.currency,
                          })
                        : undefined
                    }
                    accessory={
                      <button
                        type="button"
                        onClick={() => removeOne(r)}
                        aria-label="Remove receipt"
                        className="w-8 h-8 inline-flex items-center justify-center text-text-tertiary"
                      >
                        <Trash2 size={16} strokeWidth={1.75} />
                      </button>
                    }
                  />
                </div>
              );
            })}
          </Card>
        )}

        {receipts.length > 0 ? (
          <Button variant="destructive" fullWidth onClick={handleClearAll}>
            Clear all receipts
          </Button>
        ) : null}
      </div>
    </BottomSheet>
  );
}
